import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import queryString from 'query-string';
import { ResponseError, responseErrorsResolver } from '@ui/src';
import { useResetPasswordMutation } from '../../store/services';
import checkPasswordValidHelper from '../helpers/validate-password';

import ROUTES from '../../../routes';

const useResetPassword = () => {
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    hasUpper: false,
    hasNumber: false,
    hasSpecialCharacter: false,
  });
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const [token, setToken] = useState('');
  const [resetPassword, { isLoading, isSuccess }] = useResetPasswordMutation();
  const [globalError, setGlobalError] = useState('');

  const { search } = useLocation();
  const navigate = useNavigate();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setError,
    watch,
  } = useForm<{
    password: string;
    verifyPassword: string;
    email: string;
  }>();

  const passwordValue = watch('password');

  const onValidatePassword = (password: string) => {
    checkPasswordValidHelper(
      password,
      passwordValidation,
      setPasswordIsValid,
      passwordIsValid,
      setPasswordValidation,
    );
  };

  const onSubmit = handleSubmit(async (values) => {
    if (values.password !== values.verifyPassword) {
      setError('verifyPassword', {
        type: 'custom',
        message: 'Passwords do not match',
      });
    } else {
      const response = (await resetPassword({
        recoveryToken: token,
        password: values.password,
        email: values.email,
      })) as {
        error: ResponseError | { data: { message: string }; status: number };
      };

      if (response?.error?.data) {
        if (response?.error?.status === 403) {
          setGlobalError('Access Denied');
        } else {
          // eslint-disable-next-line no-lonely-if
          if (Array.isArray(response?.error?.data?.message)) {
            responseErrorsResolver({
              errors: response?.error as ResponseError,
              setError,
            });
          } else if (typeof response.error.data.message === 'string') {
            setGlobalError(response.error.data.message);
          }
        }
      } else {
        setGlobalError('');
      }
    }
  });

  useEffect(() => {
    if (isSuccess) {
      navigate(ROUTES.LOGIN_EMAIL);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (search) {
      const parsed = queryString.parse(search);

      if (parsed.token) {
        setToken(parsed.token as string);
      }
    }
  }, [search]);

  return {
    control,
    onSubmit,
    errors,
    passwordValue,
    passwordIsValid,
    onValidatePassword,
    passwordValidation,
    isLoading,
    globalError,
  };
};

export default useResetPassword;
